import { useMemo } from "react";
import { useUserStore } from "../store/userStore";

// Role IDs for truck yard users (from Firebase)
export const TRUCK_YARD_ADMIN_ROLE_ID = "HiLYcf6ndou53xpXNJ02";
export const TRUCK_YARD_GUARD_ROLE_ID = "KoNlfjdVBn7xGpZZNk9u";

// Inspector y Supervisor son roles distintos. Los inspectores creados antes
// tienen el id de supervisor y hay que reasignarlos al id de inspector.
export const TRUCK_YARD_INSPECTOR_ROLE_ID = "cbauTfomi9m0FgncqaFR";
export const TRUCK_YARD_SUPERVISOR_ROLE_ID = "Hh1ILyhv8I4tmZZvwwvy";

/**
 * Administrador de patios ("yard admin"), issue #558.
 *
 * El documento `roles/yardAdminRole0000001` existe en Firestore desde el
 * 29/7/2026 y hasta ahora NO estaba en el código. Eso no dejaba al rol sin
 * efecto: lo dejaba con el efecto CONTRARIO. Todos los filtros de PTAR tienen
 * la forma `isPTARCompany && (uno de los roles conocidos)` con un
 * `else if (!isPTARCompany)` del otro lado, así que un usuario de PTAR con un
 * rol desconocido no entraba en ninguna de las dos ramas y salía SIN NINGÚN
 * recorte — veía Facturación, POS y Ajustes, que el filtro le saca hasta al
 * admin de compañía. Asignar el rol desde Facturación → Usuarios (que lista
 * `roles` entero, sin filtrar) ABRÍA permisos.
 *
 * Medido el 7-sep-2026 con `_diag_yard_admin_558.js` (sólo lectura):
 *   producción (dumax-lite-production): 0 usuarios con este rol — los 5 que lo
 *     tenían se movieron al `admin` canónico ese mismo día.
 *   dev (tdc-development-846de): 3 usuarios ACTIVOS de PTAR
 *     (admin1/2/3@dumax.dev), 1 patio cada uno. O sea que el defecto está VIVO
 *     en dev y armado en prod para el próximo que lo asigne.
 *
 * El id se hardcodea igual que los otros cuatro por consistencia; el backend
 * (#561) lo resuelve por NOMBRE (`"yard admin"`) y no por id.
 */
export const TRUCK_YARD_YARD_ADMIN_ROLE_ID = "yardAdminRole0000001";

// Company ID for PTAR (Planta de Tratamiento de Agua Residual)
export const PTAR_COMPANY_ID = "zQ4Xpa88F7RstUvLKc5";

// Apps allowed for each role
// Admin: truck-yard (patios) + inspecciones (full access) + admin (fleet control, read-only)
// "lite" es el Mapa de Unidades (ruta /map), no una app recortada.
export const ADMIN_ALLOWED_APPS = [
  "truck-yard",
  "inspecciones",
  "admin",
  "lite",
];
// Inspector: only inspecciones
export const INSPECTOR_ALLOWED_APPS = ["inspecciones"];
// Guard: only truck-yard (patios) - access section only
export const GUARD_ALLOWED_APPS = ["truck-yard"];
// Supervisor: patios + inspecciones de los patios que supervisa
export const SUPERVISOR_ALLOWED_APPS = ["truck-yard", "inspecciones"];
/**
 * Admin de patios: EXACTAMENTE patios e inspecciones (política del dueño de
 * producto, 7-sep-2026, y lo que ya decía la descripción del rol en Firestore
 * desde el 29/7).
 *
 * Lo que le falta respecto del admin de compañía es deliberado y son las dos
 * apps que lo distinguen:
 *  - `lite` (/map, Mapa de Unidades): la flota es de la empresa, no del patio.
 *    Ojo: /map NO tiene guard de ruta, así que esta lista sólo lo saca del
 *    menú; lo que impide llegar por URL a las pantallas de flota es
 *    `ADMIN_APP_RESTRICTED_ROLE_IDS` (router/adminAppAccess.ts).
 *  - `admin` (Admin Flotilla): unidades, conductores, geocercas y grupos de
 *    TODA la empresa, sin recorte por patio.
 *
 * Coincide con la lista del supervisor, y aun así son constantes distintas a
 * propósito: el supervisor arrastra la validación de geocerca/horario de
 * `useAccessValidation`, que le cierra la sesión fuera del patio. Un admin de
 * patios trabaja desde una oficina. Compartir la constante invitaría a
 * "unificar" también ese comportamiento.
 */
export const YARD_ADMIN_ALLOWED_APPS = ["truck-yard", "inspecciones"];

// Menu items allowed for each role in truck yard
// Admin: all menu items (yards, personal, access, inspections)
export const ADMIN_ALLOWED_MENU = [
  "yards",
  "personal",
  "access",
  "inspections",
];
// Inspector: only inspections menu
export const INSPECTOR_ALLOWED_MENU = ["inspections"];
// Guard: only access (to their assigned yards)
export const GUARD_ALLOWED_MENU = ["access"];
// Supervisor: patios, personal, accesos e inspecciones
export const SUPERVISOR_ALLOWED_MENU = [
  "yards",
  "personal",
  "access",
  "inspections",
];
// Admin de patios: el menú COMPLETO de Patios, igual que el admin de compañía.
// La diferencia entre los dos no es qué secciones ve sino sobre qué patios
// operan: el de compañía sobre todos, éste sobre sus `yard_ids`
// (`hasYardRestrictions` lo incluye).
export const YARD_ADMIN_ALLOWED_MENU = [
  "yards",
  "personal",
  "access",
  "inspections",
];

/**
 * La tabla ÚNICA de rol de patios → apps, y su gemela de menú.
 *
 * Existía repetida como una cadena de `if/else if` en cuatro lugares
 * (`useFilteredCategories`, `PtarAppProtectedRoute`, y dos veces dentro de este
 * hook), y esa duplicación es el mecanismo exacto de #558: agregar un rol en
 * Firestore no rompía una comparación, se caía por el `else` de las cuatro
 * cadenas a la vez y el resultado era "sin recorte". Con una tabla, sumar un
 * rol es una entrada; olvidarse de una de las cuatro cadenas ya no es posible.
 *
 * Lo que la tabla NO hace, a propósito: cerrar contra CUALQUIER rol
 * desconocido de PTAR. Medido el 7-sep-2026 sobre las dos bases, PTAR tiene
 * usuarios `monitor` que no son roles de patios — 5 activos en producción, 2 en
 * dev —, y hoy pasan por el filtro de monitor (paso 3 de useFilteredCategories)
 * y salen sin recorte de patios. Cerrar por defecto los dejaría sin ninguna app
 * y con la única pista siendo un menú vacío. Por eso lo que se arregla acá es
 * que "yard admin" esté en la tabla, no que el default cambie de signo.
 */
export const PTAR_ROLE_ALLOWED_APPS: Record<string, string[]> = {
  [TRUCK_YARD_ADMIN_ROLE_ID]: ADMIN_ALLOWED_APPS,
  [TRUCK_YARD_YARD_ADMIN_ROLE_ID]: YARD_ADMIN_ALLOWED_APPS,
  [TRUCK_YARD_SUPERVISOR_ROLE_ID]: SUPERVISOR_ALLOWED_APPS,
  [TRUCK_YARD_INSPECTOR_ROLE_ID]: INSPECTOR_ALLOWED_APPS,
  [TRUCK_YARD_GUARD_ROLE_ID]: GUARD_ALLOWED_APPS,
};

export const PTAR_ROLE_ALLOWED_MENU: Record<string, string[]> = {
  [TRUCK_YARD_ADMIN_ROLE_ID]: ADMIN_ALLOWED_MENU,
  [TRUCK_YARD_YARD_ADMIN_ROLE_ID]: YARD_ADMIN_ALLOWED_MENU,
  [TRUCK_YARD_SUPERVISOR_ROLE_ID]: SUPERVISOR_ALLOWED_MENU,
  [TRUCK_YARD_INSPECTOR_ROLE_ID]: INSPECTOR_ALLOWED_MENU,
  [TRUCK_YARD_GUARD_ROLE_ID]: GUARD_ALLOWED_MENU,
};

/**
 * Las apps que puede ver el usuario, o `null` cuando este hook no opina.
 *
 * `null` significa "sin restricción por rol de patios" y cubre dos casos que NO
 * son el mismo: una empresa que no es PTAR (las apps de patios se le bloquean
 * en otro lado, por `PTAR_EXCLUSIVE_APPS`) y un usuario de PTAR con un rol que
 * no es de patios (el `monitor`). Quien llama tiene que tratar `null` como "no
 * apliques ESTE filtro", no como "permitido todo".
 */
/**
 * Si el id es uno de los roles de patios.
 *
 * Con `hasOwnProperty` y no con `table[roleId] !== undefined`: el `role_id` sale
 * de un documento de Firestore, o sea de un dato, y en un objeto literal
 * `"constructor"` o `"toString"` resuelven contra `Object.prototype` y dan algo
 * truthy. Sería un rol de patios inventado con una función por lista de apps —
 * el mismo género de "un dato decide el control de acceso" que abrió #558.
 */
export function isPtarYardRoleId(userRoleId: string): boolean {
  return Object.prototype.hasOwnProperty.call(
    PTAR_ROLE_ALLOWED_APPS,
    userRoleId,
  );
}

export function getPtarAllowedApps(
  userCompanyId: string,
  userRoleId: string,
): string[] | null {
  if (userCompanyId !== PTAR_COMPANY_ID) return null;
  if (!isPtarYardRoleId(userRoleId)) return null;
  return PTAR_ROLE_ALLOWED_APPS[userRoleId];
}

/**
 * El menú de Patios que le toca al rol, o `null` si este hook no opina.
 *
 * El guardia comprueba `PTAR_ROLE_ALLOWED_MENU` y NO `isPtarYardRoleId()`, que
 * mira la tabla de apps: son dos objetos literales distintos y comprobar uno
 * para indexar el otro es la misma "sincronización a ojo" que dejó al rol
 * "yard admin" fuera de las cuatro cadenas (#558). Con el guardia sobre la
 * tabla equivocada, un rol agregado a apps y olvidado en menú devolvía
 * `undefined` —no `null`— y `isMenuItemAllowed` lo trata como "sin
 * restricciones": fallo ABIERTO, que es exactamente lo que este PR cierra. Hoy
 * las dos tablas tienen las mismas cinco claves y no hay diferencia observable;
 * `yardAdminRole.test.ts` fija esa paridad para que siga siendo cierto.
 *
 * OJO, y por eso no alcanza con esto: al 7-sep-2026 esta función NO tiene
 * consumidores. `getAllowedMenuItems` / `isMenuItemAllowed` no se llaman desde
 * ningún componente (`grep -rn "isMenuItemAllowed" apps/v5/src` sólo da este
 * archivo). El menú de Patios lo recorta `TruckYardLayout.tsx:138-139` con su
 * PROPIA cadena de ids (supervisor y vigilante como lista de negación, todo lo
 * demás sin recorte), que no lee esta tabla y ni siquiera coincide con ella
 * —`SUPERVISOR_ALLOWED_MENU` incluye "inspections" y la del layout no—. Para el
 * admin de patios el resultado igual es el correcto (cae en "sin recorte" = el
 * menú completo, que es su lista); unificar las dos le cambiaría la pantalla al
 * SUPERVISOR, así que no se hace acá.
 */
export function getPtarAllowedMenu(
  userCompanyId: string,
  userRoleId: string,
): string[] | null {
  if (userCompanyId !== PTAR_COMPANY_ID) return null;
  if (
    !Object.prototype.hasOwnProperty.call(PTAR_ROLE_ALLOWED_MENU, userRoleId)
  ) {
    return null;
  }
  return PTAR_ROLE_ALLOWED_MENU[userRoleId];
}

/**
 * Los roles de patios que NO llevan la app `admin` (Admin Flotilla).
 *
 * Se DERIVA de la tabla en vez de escribirse a mano, y eso es la mitad del
 * arreglo de #558: `/admin` no se protege por la lista de apps sino por una
 * lista de roles restringidos en router/config.tsx, así que hasta ahora sacar
 * una app del menú y prohibirla por URL eran dos ediciones separadas — y la
 * segunda es la que nadie recuerda. Un rol nuevo que no tenga `admin` en su
 * lista queda restringido de la ruta sin tocar el router.
 */
export const PTAR_ROLE_IDS_WITHOUT_ADMIN_APP: string[] = Object.keys(
  PTAR_ROLE_ALLOWED_APPS,
).filter((roleId) => !PTAR_ROLE_ALLOWED_APPS[roleId].includes("admin"));

/**
 * Quién ve sólo los patios de su lista: supervisor, inspector, vigilante y
 * admin de patios.
 *
 * El admin de COMPAÑÍA no está, y la asimetría es deliberada (#558): su lista
 * de patios dejó de decidir qué ve —su alcance es la empresa— y en producción
 * hay 3 admins de PTAR ACTIVOS sin ningún patio, que el recorte dejaría a
 * ciegas. Para el admin de PATIOS es al revés: la lista SÍ decide, así que
 * cero patios lo manda al fallo cerrado. Esa es exactamente la razón por la
 * que el backend le exige mínimo 1 patio (#561, `ALLOWS_NO_YARDS`).
 */
export const PTAR_YARD_RESTRICTED_ROLE_IDS: string[] = [
  TRUCK_YARD_SUPERVISOR_ROLE_ID,
  TRUCK_YARD_INSPECTOR_ROLE_ID,
  TRUCK_YARD_GUARD_ROLE_ID,
  TRUCK_YARD_YARD_ADMIN_ROLE_ID,
];

/**
 * La pantalla de entrada de Patio de Maniobras: la lista de patios para quien
 * administra o supervisa (admin de compañía, admin de patios, supervisor y los
 * roles sin recorte), y Accesos para el vigilante, que es lo único que opera.
 *
 * La usan el login y el índice de `/truck-yard` (TruckYardIndexRedirect), para
 * que entrar por el menú de apps y entrar recién autenticado den lo mismo.
 */
export function resolveTruckYardHomeRoute(userRoleId: string): string {
  return userRoleId === TRUCK_YARD_GUARD_ROLE_ID
    ? "/truck-yard/access"
    : "/truck-yard/yards";
}

/**
 * A dónde aterriza cada rol de patios al iniciar sesión.
 *
 * Vive acá y no en la pantalla de login porque el login tenía su propia cadena
 * de `if/else if` sobre los mismos ids, con el mismo `else` final a `/map`. Con
 * un rol fuera de la cadena —el caso de #558— el admin de patios aterrizaba en
 * el Mapa de Unidades, que es justo la app que su rol le quita.
 */
export function resolvePtarDefaultRoute(
  userCompanyId: string,
  userRoleId: string,
): string {
  const apps = getPtarAllowedApps(userCompanyId, userRoleId);
  if (!apps) return "/map";
  if (apps.includes("truck-yard")) return resolveTruckYardHomeRoute(userRoleId);
  if (apps.includes("inspecciones")) return "/inspecciones";
  return "/map";
}

/**
 * Los patios asignados al usuario de la sesión.
 *
 * Un solo campo para los cuatro roles (issue #497). Antes esto se bifurcaba:
 * `userYardIds` para admin/supervisor/inspector y `userYardId` para el
 * vigilante — pero el login rellenaba `userYardIds` con `yard_ids`, que el
 * documento de un supervisor no tenía (tenía `yard_id`), así que quedaba `[]`.
 * Con la lista vacía todos los filtros de la forma
 * `hasYardRestrictions() && assignedYardIds.length > 0` se apagaban y el
 * supervisor veía TODOS los patios: el sistema fallaba abierto.
 *
 * `userYardId` se sigue leyendo como respaldo, y NO es decorativo: localStorage
 * sobrevive al deploy, así que una sesión abierta desde antes de este cambio
 * tiene el singular escrito y el plural en `[]`. Sin el respaldo esas sesiones
 * se quedarían sin patios hasta el próximo login — que ahora, al fallar
 * cerrado, significa no ver nada. Se puede borrar cuando no queden sesiones
 * viejas vivas.
 */
export function getUserAssignedYardIds(): string[] {
  try {
    const raw = localStorage.getItem("userYardIds");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.filter(Boolean);
      }
    }
  } catch (e) {
    console.error("Error parsing userYardIds:", e);
  }
  const legacySingle = localStorage.getItem("userYardId");
  return legacySingle ? [legacySingle] : [];
}

/**
 * El alcance de patios de la sesión, como TRES estados.
 *
 * Un booleano no alcanza y ya costó tres defectos seguidos: "restringido sin
 * patios" y "sin restricción" se colapsaban en el mismo `[]`/`undefined`/`""`,
 * y de ahí salieron el recorte que se apagaba solo, el `lockedYardId` que
 * desbloqueaba el combo de patios, y un centinela truthy que llegó a permitir
 * persistir un evento de acceso bajo un patio inexistente.
 *
 *   all  - sin recorte (admin de COMPAÑÍA, otras compañías). `[]` NO significa
 *          "sin patios" acá. Acá decía "admin de patios", y desde #558 esa
 *          frase nombra al rol CONTRARIO: el admin de patios es de los cuatro
 *          que SÍ se recortan (`PTAR_YARD_RESTRICTED_ROLE_IDS`), así que nunca
 *          llega a `all`.
 *   some - recortado, con patios concretos.
 *   none - recortado y SIN patios: no ve nada, no puede crear nada, y la
 *          pantalla tiene que decir por qué.
 */
export type YardScope =
  | { mode: "all" }
  | { mode: "some"; yardIds: string[] }
  | { mode: "none" };

export function resolveYardScope(
  hasRestrictions: boolean,
  assignedYardIds: string[],
): YardScope {
  if (!hasRestrictions) return { mode: "all" };
  // Se limpia antes de decidir: un id vacío no es un patio, y dejarlo pasar
  // produciría un `some` con basura que después nadie puede resolver.
  const yardIds = assignedYardIds.map((id) => id?.trim()).filter(Boolean);
  if (yardIds.length === 0) return { mode: "none" };
  return { mode: "some", yardIds };
}

/**
 * Restringido y SIN ningún patio, decidido con la misma normalización que
 * `resolveYardScope` — de ahí que delegue en él en vez de repetir el criterio.
 *
 * No es simetría decorativa: la versión anterior comparaba `length === 0` sobre
 * la lista cruda, así que un `yard_ids: ["  "]` daba scope `none` (la pantalla
 * queda vacía) y a la vez `false` acá (el cartel que explica por qué no se
 * muestra). El usuario veía una lista vacía sin motivo, que es exactamente el
 * síntoma que #497 vino a eliminar. Los ids con espacios son alcanzables: la
 * ficha de personal los guarda tal cual vienen del formulario.
 */
export function hasNoAssignedYardsFor(
  hasRestrictions: boolean,
  assignedYardIds: string[],
): boolean {
  return resolveYardScope(hasRestrictions, assignedYardIds).mode === "none";
}

/**
 * Los patios de una lista que caen dentro del alcance.
 *
 * Una sola función para el combo de filtro y para los `<select>` de los modales.
 * Estaban separados y divergieron: `selectableYards` recortaba y `yardOptions`
 * listaba TODOS, así que un usuario restringido podía elegir en el filtro un
 * patio cuyos eventos el propio filtro ya descartaba — cero resultados, que en
 * Accesos se lee como "no hubo movimiento" y no como "no es tuyo".
 *
 * Con `all` devuelve la MISMA referencia, no una copia: quien la llama la usa
 * como dependencia de `useMemo`.
 */
export function filterYardsByScope<T extends { id: string }>(
  scope: YardScope,
  yards: T[],
): T[] {
  return filterByYardScope(scope, yards, (y) => y.id);
}

/**
 * Lo mismo, para filas que NO son patios y llevan el patio en otro campo.
 *
 * Existe porque el historial de inspecciones filtra `yardInspections` por
 * `yard_id` y tenía su propio `if (!hasYardRestrictions()) ... .filter(...)`
 * escrito a mano: el mismo switch de tres estados, duplicado. Duplicarlo es
 * exactamente cómo apareció #497 —la rama "restringido y sin patios" colapsada
 * contra "sin restricción", o sea fallo ABIERTO—, así que hay un solo lugar
 * donde se decide y dos accesores.
 *
 * Con `all` devuelve la MISMA referencia, no una copia: quien la llama la usa
 * como dependencia de `useMemo`, y copiar acá volvería a romper aguas abajo lo
 * que el memo viene a proteger.
 */
export function filterByYardScope<T>(
  scope: YardScope,
  rows: T[],
  yardIdOf: (row: T) => string,
): T[] {
  if (scope.mode === "all") return rows;
  if (scope.mode === "none") return [];
  return rows.filter((row) => scope.yardIds.includes(yardIdOf(row)));
}

export interface YardFilterDecision<T> {
  /** Los patios que el combo ofrece, ya recortados al alcance. */
  options: T[];
  /**
   * El patio EFECTIVO. `""` significa "todos los del alcance", y sólo es
   * alcanzable cuando `allowAll`.
   */
  selectedYardId: string;
  /** El combo puede quedar en "todos" (todos los MÍOS, no los de la empresa). */
  allowAll: boolean;
  /** No hay nada que elegir: se dibuja deshabilitado. */
  locked: boolean;
}

/**
 * Qué ofrece y qué tiene puesto el combo de patio (issue #496).
 *
 * El bug que cierra: las tres pantallas con combo de patio arrancaban en
 * `selectedYard = ""` con el placeholder "Todos los patios", y aguas abajo
 * `""` se traduce literalmente en `...(selectedYard ? { yard_id } : {})`. O sea
 * que el estado inicial de un vigilante —cuyo único menú es Accesos— pedía al
 * backend los eventos de los SIETE patios de la empresa, y el recorte por
 * patio asignado recién ocurría en el navegador, sobre filas ya viajadas. Para
 * un inspector el síntoma era el opuesto y peor de leer: el combo listaba
 * patios ajenos y elegir uno devolvía cero filas, que en estas pantallas se lee
 * como "ese patio no tuvo inspecciones" y no como "no es tuyo".
 *
 * Se resuelve en RENDER, no en un `useEffect` que corrija el estado después. Un
 * efecto deja un commit intermedio con `selectedYard = ""`, y los efectos que
 * disparan las cargas corren en ESE commit: la primera petición saldría igual
 * sin `yard_id`, que es exactamente lo que hay que impedir. Por eso también el
 * respaldo cuando el catálogo todavía no llegó es `scope.yardIds[0]` y no `""`:
 * `fetchTruckYardOptions()` resuelve uno o dos renders después del montaje, y
 * en ese hueco `yards` está vacío — el id del alcance no necesita el catálogo,
 * sólo el nombre lo necesita.
 *
 * `canSelectAllYards` no se deduce del alcance: un supervisor y un inspector
 * pueden tener el MISMO `some` de dos patios y aun así el supervisor puede ver
 * el agregado de los suyos y el inspector no (criterios 2 y 3 de #496). Es una
 * decisión de rol, y llega por parámetro para que esta función siga siendo pura.
 *
 * El orden de `options` lo fija el catálogo y no `scope.yardIds`, para que el
 * patio que arranca elegido sea el primero que el usuario ve en la lista. Eso
 * tiene un costo conocido y se elige igual: mientras el catálogo no llegó el
 * respaldo es `scope.yardIds[0]` (orden de localStorage) y después pasa a ser
 * `options[0]` (orden del catálogo, `createdAt desc`), así que un usuario con
 * DOS o más patios asignados puede lanzar la primera petición con un patio suyo
 * y reemplazarla enseguida por otra con el otro. No hay fuga —los dos son
 * suyos— y `filterGenerationRef` descarta la respuesta vieja; el saldo es una
 * petición de más al montar. Con un solo patio (el vigilante, el caso que
 * #496 llama "el peor") no pasa: los dos órdenes coinciden.
 */
export function resolveYardFilter<T extends { id: string }>(input: {
  scope: YardScope;
  canSelectAllYards: boolean;
  yards: T[];
  /** Lo último que eligió el usuario; puede haber quedado fuera del alcance. */
  selectedYardId: string;
}): YardFilterDecision<T> {
  const { scope, yards, selectedYardId } = input;
  const options = filterYardsByScope(scope, yards);

  // Sin patios asignados no hay ni "todos" ni "uno": el fallo cerrado de #497.
  // Devolver "" con `allowAll` sería pedir la empresa entera, y devolver un id
  // sería inventar un patio — el centinela truthy que en su momento llegó a
  // persistir un evento bajo un patio inexistente.
  if (scope.mode === "none") {
    return { options: [], selectedYardId: "", allowAll: false, locked: true };
  }

  const allowAll = input.canSelectAllYards;
  const inScope =
    !!selectedYardId &&
    (scope.mode === "all"
      ? // Con el catálogo todavía vacío no hay con qué desmentir la selección, y
        // descartarla ahí le borraría el patio elegido al admin en cada montaje.
        yards.length === 0 || options.some((y) => y.id === selectedYardId)
      : scope.yardIds.includes(selectedYardId));

  if (allowAll) {
    return {
      options,
      selectedYardId: inScope ? selectedYardId : "",
      allowAll: true,
      locked: false,
    };
  }

  const fallback =
    options[0]?.id ?? (scope.mode === "some" ? scope.yardIds[0] : "");
  return {
    options,
    selectedYardId: inScope ? selectedYardId : fallback,
    allowAll: false,
    // Sólo se traba cuando el alcance deja un patio y nada más. Con `all` no se
    // traba nunca: el catálogo no está recortado y el combo tiene que seguir
    // siendo usable. Ese cruce —`all` sin "todos"— hoy no es alcanzable, porque
    // las dos decisiones cuelgan del mismo `isPTARCompany` (`hasYardRestrictions`
    // y `canSelectAllYards`) y fuera de PTAR ambas dan "sin recorte"; se
    // contempla igual para que la función sea total y no dependa de que esa
    // coincidencia siga siendo cierta.
    locked: scope.mode === "some" && scope.yardIds.length <= 1,
  };
}

export interface YardScopedRequest<T extends { id: string }>
  extends YardFilterDecision<T> {
  /**
   * Los params de patio de la petición: `{}` o `{ yard_id }`. Se mezclan con
   * spread, que es la forma en que las dos pantallas ya los armaban.
   */
  params: { yard_id?: string };
  /** Si la petición debe salir siquiera. */
  enabled: boolean;
}

/**
 * Qué `yard_id` lleva la petición, y si sale (issue #496).
 *
 * Es el ÚLTIMO paso de la cadena de #496 —rol → alcance → combo → cable— y el
 * único que hasta ahora no tenía test. `resolveYardFilter` estaba cubierto,
 * incluido el caso del montaje (catálogo vacío, selección en ""), pero el salto
 * de su salida a los params estaba escrito a mano y por duplicado en las dos
 * pantallas: `...(selectedYard ? { yard_id: selectedYard } : {})` junto a un
 * `enabled`/`skipYardQueries` derivado por separado del alcance. Dos
 * expresiones que tienen que decir lo mismo y nada las ataba.
 *
 * `{}` con la petición HABILITADA significa "todos los patios de la empresa", y
 * eso NO es siempre un error: es lo correcto para el admin, cuyo alcance ES la
 * empresa, y es lo que hoy hace el "todos" del supervisor, que recorta el combo
 * pero no la petición (el recorte a los suyos lo sigue haciendo el navegador —
 * ver el test que lo documenta). Donde sí es un error es con alcance `none`:
 * ahí `{}` habilitado son hasta 1000 eventos cada 5 s para dibujar una lista
 * vacía debajo del cartel de "no tenés patios asignados". Y con un vigilante en
 * el primer render —antes de que llegue el catálogo— serían los siete patios en
 * el instante en que la pantalla se ve por primera vez, que es lo que evita el
 * respaldo `scope.yardIds[0]` de `resolveYardFilter`.
 *
 * Por eso `enabled` sale de `scope.mode` y no de `selectedYardId === ""`: los
 * dos casos de `{}` no son el mismo, y colapsarlos apagaría la pantalla del
 * admin. Lo que juntar las dos mitades acá compra no es un invariante nuevo:
 * es que no puedan desincronizarse al editarse por separado, que es como
 * estaban —cuatro copias escritas a mano entre las dos pantallas.
 *
 * Lo que esta función NO puede garantizar: que la pantalla la llame y meta el
 * resultado en la query-key. `inspectionHistorySection.tsx` y
 * `accessSection/page.tsx` importan transitivamente `utils/api` → firebase y no
 * colectan sin `apps/v5/.env`, que un worktree no recibe. Por eso la decisión
 * está acá y el cableado queda como una línea a la vista en el diff.
 *
 * Desde que los dos combos son de selección múltiple (#708 Accesos, #713
 * Historial de Inspecciones), ninguna pantalla llama a ESTA función: las dos
 * pasan por `resolveMultiYardScopedRequest`. Queda como el contrato escalar que
 * yardScope.test.ts fija; lo que protege a las pantallas hoy es
 * `resolveYardFilter` —en la que la versión múltiple delega— y
 * multiYardScope.test.ts.
 */
export function resolveYardScopedRequest<T extends { id: string }>(input: {
  scope: YardScope;
  canSelectAllYards: boolean;
  yards: T[];
  selectedYardId: string;
}): YardScopedRequest<T> {
  const filter = resolveYardFilter(input);
  return {
    ...filter,
    params: filter.selectedYardId ? { yard_id: filter.selectedYardId } : {},
    enabled: input.scope.mode !== "none",
  };
}

export interface MultiYardScopedRequest<T extends { id: string }> {
  /** Los patios que el combo ofrece, ya recortados al alcance. */
  options: T[];
  /**
   * Los patios EFECTIVOS, en el orden en que se eligieron. `[]` significa
   * "todos los del alcance" y sólo es alcanzable cuando `allowAll`.
   */
  selectedYardIds: string[];
  allowAll: boolean;
  locked: boolean;
  /**
   * Params de patio de la petición: `{}`, `{ yard_id }` con uno solo (el
   * contrato de siempre, que entiende cualquier backend desplegado) o
   * `{ yard_ids: "a,b" }` con varios.
   */
  params: { yard_id?: string; yard_ids?: string };
  enabled: boolean;
}

/**
 * La versión de SELECCIÓN MÚLTIPLE de `resolveYardScopedRequest`, para los
 * combos de patios de Accesos y del Historial de Inspecciones, que permiten
 * elegir varios.
 *
 * No reescribe la decisión: delega en `resolveYardFilter` todo lo que ya estaba
 * resuelto (opciones recortadas, "todos" sí o no, combo trabado, respaldo al
 * patio del alcance mientras el catálogo no llegó) y sólo agrega el recorte de
 * una LISTA en vez de un id. Las mismas reglas de #496/#497, elemento a
 * elemento:
 *
 *   · Un id fuera del alcance se descarta, igual que el escalar.
 *   · Si no queda ninguno: con `allowAll` es "todos" (`[]`); sin `allowAll`
 *     cae al MISMO respaldo que el escalar (`options[0]`, o
 *     `scope.yardIds[0]` con el catálogo vacío), para que un inspector o un
 *     vigilante nunca pidan la empresa entera, tampoco al vaciar el combo.
 *   · Con `none` no hay nada que elegir y la petición no sale.
 *
 * Con un solo patio los params son `{ yard_id }` —byte a byte lo que mandaba
 * la versión escalar—, así que el caso de siempre no depende de que el backend
 * ya entienda `yard_ids`.
 */
export function resolveMultiYardScopedRequest<T extends { id: string }>(input: {
  scope: YardScope;
  canSelectAllYards: boolean;
  yards: T[];
  selectedYardIds: readonly string[];
}): MultiYardScopedRequest<T> {
  const { scope, yards } = input;
  // Con "" la función escalar devuelve lo que corresponde a "nada elegido":
  // "" si `allowAll`, o el patio de respaldo si no.
  const base = resolveYardFilter({
    scope,
    canSelectAllYards: input.canSelectAllYards,
    yards,
    selectedYardId: "",
  });

  if (scope.mode === "none") {
    return {
      options: [],
      selectedYardIds: [],
      allowAll: false,
      locked: true,
      params: {},
      enabled: false,
    };
  }

  const seen = new Set<string>();
  const inScope = input.selectedYardIds.filter((id) => {
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return scope.mode === "all"
      ? // Mismo criterio que el escalar: con el catálogo vacío no hay con qué
        // desmentir la selección.
        yards.length === 0 || base.options.some((y) => y.id === id)
      : scope.yardIds.includes(id);
  });

  const selectedYardIds =
    inScope.length > 0
      ? inScope
      : base.selectedYardId
        ? [base.selectedYardId]
        : [];

  const params: { yard_id?: string; yard_ids?: string } =
    selectedYardIds.length === 0
      ? {}
      : selectedYardIds.length === 1
        ? { yard_id: selectedYardIds[0] }
        : { yard_ids: selectedYardIds.join(",") };

  return {
    options: base.options,
    selectedYardIds,
    allowAll: base.allowAll,
    locked: base.locked,
    params,
    enabled: true,
  };
}

/**
 * El `userYardId` que la sesión guarda como respaldo legacy.
 *
 * Sólo existe para las sesiones de UN patio (ver `getUserAssignedYardIds`: se
 * lee cuando `userYardIds` falta o viene vacío). Con varios patios no se elige
 * ninguno, por la misma razón que `getAssignedYardsLabel` no nombra uno: en
 * producción los 5 supervisores tienen los 7 patios y los cinco comparten el
 * mismo `yard_ids[0]`, así que escribir el elemento 0 le fijaba a los cinco el
 * mismo patio arbitrario — y ese valor sobrevive al deploy en localStorage.
 */
export function getPrimaryYardIdForSession(assignedYardIds: string[]): string {
  const scope = resolveYardScope(true, assignedYardIds);
  if (scope.mode !== "some" || scope.yardIds.length !== 1) return "";
  return scope.yardIds[0];
}

/**
 * Cómo se nombra en el encabezado el alcance de patios del usuario.
 *
 * Con un solo patio va el nombre, como siempre. Con varios NO se puede nombrar
 * uno: hasta #497 la lista tenía sólo el escalar —el patio primario— así que
 * `yards.find(...)` acertaba por construcción; ahora tiene todos los asignados
 * y ese `find` devolvía el primero por orden de `createdAt desc`, o sea un
 * patio arbitrario. En producción los 5 supervisores tienen los 7 patios, así
 * que el encabezado habría mostrado siempre el mismo nombre equivocado
 * justamente a los usuarios multi-patio que la feature habilita.
 *
 * Devuelve "" cuando no hay nada que nombrar, para que quien llama conserve el
 * valor anterior en vez de pisarlo con vacío.
 */
export function getAssignedYardsLabel(
  assignedYardIds: string[],
  yards: { id: string; name?: string }[],
): string {
  if (assignedYardIds.length === 0 || yards.length === 0) return "";

  const names = assignedYardIds
    .map((id) => yards.find((y) => y.id === id)?.name)
    .filter((n): n is string => Boolean(n));

  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  return `${names.length} patios`;
}

/**
 * Hook to check if the current user has a truck yard specific role
 * and get their permissions based on role ID
 */
export function useTruckYardRole() {
  const user = useUserStore((state) => state.user);

  const userRoleId = user?.role_id || localStorage.getItem("userRoleId") || "";
  const userCompanyId =
    user?.company_id ||
    localStorage.getItem("userCompanyId") ||
    localStorage.getItem("companyId") ||
    "";

  // Check if user belongs to PTAR company
  const isPTARCompany = userCompanyId === PTAR_COMPANY_ID;

  const isAdmin = userRoleId === TRUCK_YARD_ADMIN_ROLE_ID;
  const isInspector = userRoleId === TRUCK_YARD_INSPECTOR_ROLE_ID;
  const isSupervisor = userRoleId === TRUCK_YARD_SUPERVISOR_ROLE_ID;
  const isGuard = userRoleId === TRUCK_YARD_GUARD_ROLE_ID;
  const isYardAdmin = userRoleId === TRUCK_YARD_YARD_ADMIN_ROLE_ID;

  // User is a truck yard user if they have any of these roles AND belong to PTAR company
  const isTruckYardUser =
    isPTARCompany && isPtarYardRoleId(userRoleId);

  // Los roles restringidos leen todos el mismo campo (issue #497). Para un rol
  // sin restricción devuelve [], pero eso NO significa "sin patios": lo que
  // decide si hay recorte es hasYardRestrictions(), y hay que consultarlo
  // primero. Una lista vacía en un rol restringido significa "sin patios
  // asignados" y tiene que dejar al usuario sin ver nada.
  const getAssignedYardIds = (): string[] => {
    if (!hasYardRestrictions()) return [];
    return getUserAssignedYardIds();
  };

  /**
   * Quién ve sólo sus patios: supervisor, inspector, vigilante y —desde #558—
   * el admin de PATIOS. La lista vive en PTAR_YARD_RESTRICTED_ROLE_IDS.
   *
   * El admin de COMPAÑÍA (truck-yard admin) NO está: su alcance es la empresa,
   * no una lista de
   * patios. Estuvo acá hasta que el fallo cerrado de #497 lo volvió peligroso,
   * porque con el recorte encendido y `yard_ids` vacío dejaba de ver todo.
   * Medido contra las dos bases (script read-only sobre `users`, filtrando por
   * company_id de PTAR y descartando borrados):
   *
   *   producción (dumax-lite-production)  admin: 8 en PTAR,  3 sin patio
   *     general1/2/3.acuapue@dumaxst.com — yard_ids y yard_id ambos undefined,
   *     status ACTIVE (R5tW0uX4yY9cF6dH2aI8)
   *   dev (tdc-development-846de)         admin: 14 en PTAR, 5 sin patio
   *   supervisor / inspector / guard: 0 sin patio en AMBAS bases
   *
   * O sea: los únicos usuarios reales que el fallo cerrado dejaba afuera eran
   * admins, y sin recuperación desde la app — Personal también les quedaba
   * vacío, y un patio que crearan desaparecía de su propia lista porque el alta
   * no agrega el id a los `yard_ids` del creador. La migración tampoco los
   * siembra (`planFor` → skip, deliberado: el rol "admin" tiene 337 usuarios en
   * prod y sólo 8 son de PTAR).
   *
   * Si algún día el admin de COMPAÑÍA tiene que verse recortado, el cambio es
   * volver a agregarlo acá Y sembrarle `yard_ids` a esos tres primero. (Acá
   * decía "el admin de patios", que desde #558 es el rol de al lado y que YA
   * está en la lista: leído hoy, esta nota parecía una tarea pendiente que en
   * realidad se hizo sobre el otro rol.)
   *
   * El `isPTARCompany` es tan necesario como los roles, y sólo se nota ahora que
   * el recorte falla CERRADO. Los role_id no son exclusivos de PTAR: en
   * producción el rol "admin" tiene 337 usuarios y sólo 8 son de PTAR. Sin esta
   * comprobación, cualquier usuario de otra compañía que tuviera el role_id de
   * guard/supervisor/inspector entraría al recorte, leería `userYardIds` — que
   * su login nunca escribe, porque ese bloque también filtra por rol de patio —
   * y vería todas las listas vacías. `isTruckYardUser` ya combinaba las dos
   * condiciones; esto lo alinea. Toda la evidencia de arriba está medida
   * filtrando por company_id de PTAR, así que no dice nada sobre las otras
   * compañías: acá se las deja explícitamente fuera del recorte.
   */
  const hasYardRestrictions = (): boolean => {
    if (!isPTARCompany) return false;
    return PTAR_YARD_RESTRICTED_ROLE_IDS.includes(userRoleId);
  };

  /**
   * El usuario está restringido por patio y no tiene ninguno asignado.
   *
   * Es la condición que antes apagaba el recorte y ahora lo endurece: quien
   * caiga acá no debe ver nada, y la pantalla debe explicar por qué en vez de
   * mostrar una lista vacía sin motivo.
   */
  /**
   * El alcance como objeto ESTABLE entre renders.
   *
   * `getYardScope()` construía uno nuevo en cada llamada, y en accessSection es
   * la dependencia de siete `useMemo` encadenados — `baseFiltered`, `filtered`,
   * `exportRows`, `alertCounts`, `filteredIds`, `selectableYards` y
   * `yardOptions` —: con una referencia nueva por render ninguno memoizaba, y
   * esa pantalla tiene poll cada 5 s sobre una lista de hasta 1000 eventos
   * (`limit` de loadEvents). Antes de #497 el recorte entraba como `string[]`
   * derivado y esas dependencias eran estables; el objeto de tres estados lo
   * rompió sin que se notara.
   *
   * La clave del memo se lee de localStorage porque de ahí sale el dato
   * (`getUserAssignedYardIds`), y localStorage no notifica cambios: dos `getItem`
   * por render son baratos comparados con recalcular el pipeline, y son lo que
   * hace que el scope siga al login sin quedar congelado.
   */
  const restricted = hasYardRestrictions();
  const assignedYardIdsKey = `${localStorage.getItem("userYardIds") || ""}|${
    localStorage.getItem("userYardId") || ""
  }`;
  const yardScope = useMemo(
    () => resolveYardScope(restricted, getUserAssignedYardIds()),
    [restricted, assignedYardIdsKey],
  );

  const hasNoAssignedYards = (): boolean =>
    hasNoAssignedYardsFor(hasYardRestrictions(), getAssignedYardIds());

  /**
   * Si el combo de patio puede quedar en "todos" (issue #496, criterios 2 y 3).
   *
   * El inspector y el vigilante NO: su pantalla siempre describe UN patio.
   * El supervisor sí, y su "todos" abarca sólo los suyos porque las opciones ya
   * vienen recortadas por `resolveYardFilter`; el admin de COMPAÑÍA también,
   * pero por otro motivo: su alcance ES la empresa, así que su "todos" son
   * todos de verdad.
   *
   * El admin de PATIOS (#558) también puede, y va por el camino del supervisor
   * y no por el del admin de compañía: está recortado
   * (`PTAR_YARD_RESTRICTED_ROLE_IDS`), así que su "todos" es el agregado de sus
   * `yard_ids` y nada más. Acá decía "el admin de patios ... porque su alcance
   * es la empresa", que desde #558 nombra al rol equivocado Y da la razón
   * equivocada.
   *
   * No es lo mismo que `hasYardRestrictions()` y por eso es una función aparte:
   * el supervisor está recortado y aun así puede ver el agregado. Colapsarlas
   * en un booleano solo le sacaría al supervisor la vista de conjunto de sus
   * siete patios, que es justamente para lo que existe el rol.
   *
   * El gate por compañía es el mismo que en `hasYardRestrictions`, y por la
   * misma razón medida: los role_id no son exclusivos de PTAR —en producción el
   * rol "admin" tiene 337 usuarios y sólo 8 son de PTAR—, así que sin él un
   * usuario de otra empresa con el role_id de vigilante quedaría con el combo
   * trabado en el primer patio de un catálogo que su alcance (`all`) ni
   * siquiera recorta.
   */
  const canSelectAllYards = (): boolean => {
    if (!isPTARCompany) return true;
    return !(isInspector || isGuard);
  };

  // Check if a specific yard is allowed
  const isYardAllowed = (yardId: string): boolean => {
    if (!hasYardRestrictions()) return true;
    const assignedYardIds = getAssignedYardIds();
    return assignedYardIds.includes(yardId);
  };

  // Get allowed apps based on role (only for PTAR company users)
  const getAllowedApps = (): string[] | null =>
    getPtarAllowedApps(userCompanyId, userRoleId);

  // Get allowed menu items based on role (only for PTAR company users)
  const getAllowedMenuItems = (): string[] | null =>
    getPtarAllowedMenu(userCompanyId, userRoleId);

  // Check if a specific app is allowed
  const isAppAllowed = (appId: string): boolean => {
    const allowedApps = getAllowedApps();
    if (!allowedApps) return true; // No restrictions
    return allowedApps.includes(appId);
  };

  // Check if a specific menu item is allowed
  const isMenuItemAllowed = (menuId: string): boolean => {
    const allowedMenuItems = getAllowedMenuItems();
    if (!allowedMenuItems) return true; // No restrictions
    return allowedMenuItems.includes(menuId);
  };

  // Get the default route for truck yard roles (only for PTAR company users)
  const getDefaultRoute = (): string =>
    resolvePtarDefaultRoute(userCompanyId, userRoleId);

  return {
    userRoleId,
    userCompanyId,
    isPTARCompany,
    isAdmin,
    isInspector,
    isSupervisor,
    isGuard,
    isYardAdmin,
    isTruckYardUser,
    getAllowedApps,
    getAllowedMenuItems,
    isAppAllowed,
    isMenuItemAllowed,
    getDefaultRoute,
    getAssignedYardIds,
    hasYardRestrictions,
    hasNoAssignedYards,
    canSelectAllYards,
    /** El alcance de patios como tres estados; ver resolveYardScope. */
    getYardScope: (): YardScope => yardScope,
    isYardAllowed,
  };
}

export default useTruckYardRole;
